import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { IntercomMessenger } from "./components/IntercomMessenger";
import { PortalLayout } from "./components/PortalLayout";
import { Landing } from "./pages/Landing";
import { Features } from "./pages/Features";
import { Pricing } from "./pages/Pricing";
import { Privacy } from "./pages/Privacy";
import { Terms } from "./pages/Terms";
import { Signup } from "./pages/Signup";
import { Welcome } from "./pages/Welcome";
import { PinRecovery } from "./pages/PinRecovery";
import { EmailSignature } from "./pages/EmailSignature";
import { HelpHub } from "./pages/help/HelpHub";
import { GuidesIndex } from "./pages/help/GuidesIndex";
import { GuidePage } from "./pages/help/GuidePage";
import { FaqPage } from "./pages/help/FaqPage";
import { Login } from "./pages/portal/Login";
import { ResetPassword } from "./pages/portal/ResetPassword";
import { Dashboard } from "./pages/portal/Dashboard";
import { Downloads } from "./pages/portal/Downloads";
import { Settings } from "./pages/portal/Settings";
import { AdminLayout } from "./components/AdminLayout";
import { Overview as AdminOverview } from "./pages/admin/Overview";
import { Users as AdminUsers } from "./pages/admin/Users";
import { UserDetail as AdminUserDetail } from "./pages/admin/UserDetail";
import { TransferRequests } from "./pages/admin/TransferRequests";
import { Activations } from "./pages/admin/Activations";
import { Licenses } from "./pages/admin/Licenses";
import { GiftKey } from "./pages/admin/GiftKey";
import { SignupAllowlist } from "./pages/admin/SignupAllowlist";
import { Waitlist } from "./pages/admin/Waitlist";
import { useAuthStore } from "./stores/auth";

export function App() {
  const location = useLocation();
  const isPortal = location.pathname === "/portal" || location.pathname.startsWith("/portal/");
  const isAdmin = location.pathname.startsWith("/admin");
  const isLogin = location.pathname === "/portal/login";
  const initialize = useAuthStore((s) => s.initialize);

  useEffect(() => {
    const unsubscribe = initialize();
    return unsubscribe;
  }, [initialize]);

  return (
    <div className="flex min-h-screen flex-col font-body">
      {!["/welcome", "/reset-password"].includes(location.pathname) && <IntercomMessenger />}
      <Navbar />

      <div className="flex-1">
        <Routes>
          {/* Marketing pages */}
          <Route path="/" element={<Landing />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/pin-recovery" element={<PinRecovery />} />
          <Route path="/tools/email-signature" element={<EmailSignature />} />

          {/* Help / support */}
          <Route path="/help" element={<HelpHub />} />
          <Route path="/help/guides" element={<GuidesIndex />} />
          <Route path="/help/guides/:slug" element={<GuidePage />} />
          <Route path="/help/faq" element={<FaqPage />} />

          {/* Portal */}
          <Route path="/portal/login" element={<Login />} />
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="downloads" element={<Downloads />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* Admin (@roguelite.tech only) */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:id" element={<AdminUserDetail />} />
            <Route path="transfers" element={<TransferRequests />} />
            <Route path="activations" element={<Activations />} />
            <Route path="licenses" element={<Licenses />} />
            <Route path="gift" element={<GiftKey />} />
            <Route path="allowlist" element={<SignupAllowlist />} />
            <Route path="waitlist" element={<Waitlist />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>

      {/* Footer only on marketing pages */}
      {(!isPortal || isLogin) && !isAdmin && <Footer />}
    </div>
  );
}
